import i18n from '@i18n';

/**
 * IndexedDB 管理器 - 用于存储视频截图数据
 */

// 数据库配置
const DB_CONFIG = {
  name: 'VideoCaptureDB',
  version: 1,
  stores: {
    snapshots: { keyPath: 'id' }
  }
};

class IndexedDBManager {
  constructor() {
    this.db = null;
    this.isInitialized = false;
  }

  /**
   * 初始化数据库
   * @returns {Promise<IDBDatabase>} 返回初始化的数据库对象
   */
  async initDB() {
    if (this.isInitialized && this.db) {
      return this.db;
    }

    return new Promise((resolve, reject) => {
      // 检查浏览器支持
      if (!window.indexedDB) {
        reject(new Error(i18n.t('editor:utils.indexedDB.browserNotSupported')));
        return;
      }

      const request = window.indexedDB.open(DB_CONFIG.name, DB_CONFIG.version);

      // 数据库升级或创建时
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // 创建存储对象
        if (!db.objectStoreNames.contains('snapshots')) {
          db.createObjectStore('snapshots', { keyPath: 'id' });
        }
      };

      request.onerror = (event) => {
        console.error(i18n.t('editor:utils.indexedDB.openDBFailed'), event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        this.isInitialized = true;
        
        this.db.onerror = (event) => {
          console.error(i18n.t('editor:utils.indexedDB.dbError'), event.target.error);
        };
        
        resolve(this.db);
      };
    });
  }

  /**
   * 存储截图数据
   * @param {Object} snapshot - 截图对象
   * @returns {Promise<string>} 返回存储的截图ID
   */
  async saveSnapshot(snapshot) {
    try {
      const db = await this.initDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['snapshots'], 'readwrite');
        const store = transaction.objectStore('snapshots');
        
        const request = store.put(snapshot);
        
        request.onerror = (event) => {
          console.error(i18n.t('editor:utils.indexedDB.saveSnapshotFailed'), event.target.error);
          reject(event.target.error);
        };
        
        request.onsuccess = () => {
          resolve(snapshot.id);
        };
      });
    } catch (error) {
      console.error(i18n.t('editor:utils.indexedDB.saveSnapshotError'), error);
      throw error;
    }
  }

  /**
   * 批量存储截图数据
   * @param {Array<Object>} snapshots - 截图对象数组
   * @returns {Promise<Array<string>>} 返回存储的截图ID数组
   */
  async saveSnapshots(snapshots) {
    try {
      const db = await this.initDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['snapshots'], 'readwrite');
        const store = transaction.objectStore('snapshots');
        const results = [];
        
        transaction.onerror = (event) => {
          console.error(i18n.t('editor:utils.indexedDB.batchSaveFailed'), event.target.error);
          reject(event.target.error);
        };
        
        transaction.oncomplete = () => {
          resolve(results);
        };
        
        snapshots.forEach(snapshot => {
          const request = store.put(snapshot);
          request.onsuccess = () => {
            results.push(snapshot.id);
          };
        });
      });
    } catch (error) {
      console.error(i18n.t('editor:utils.indexedDB.batchSaveError'), error);
      throw error;
    }
  }

  /**
   * 获取所有截图数据
   * @returns {Promise<Array<Object>>} 返回所有截图对象
   */
  async getAllSnapshots() {
    try {
      const db = await this.initDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['snapshots'], 'readonly');
        const store = transaction.objectStore('snapshots');
        const request = store.getAll();
        
        request.onerror = (event) => {
          console.error(i18n.t('editor:utils.indexedDB.getSnapshotFailed'), event.target.error);
          reject(event.target.error);
        };
        
        request.onsuccess = (event) => {
          resolve(event.target.result || []);
        };
      });
    } catch (error) {
      console.error(i18n.t('editor:utils.indexedDB.getSnapshotError'), error);
      throw error;
    }
  }

  /**
   * 根据ID获取截图数据
   * @param {string} id - 截图ID
   * @returns {Promise<Object|null>} 返回截图对象或null
   */
  async getSnapshot(id) {
    try {
      const db = await this.initDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['snapshots'], 'readonly');
        const store = transaction.objectStore('snapshots');
        const request = store.get(id);
        
        request.onerror = (event) => {
          console.error(i18n.t('editor:utils.indexedDB.getSnapshotFailed'), event.target.error);
          reject(event.target.error);
        };
        
        request.onsuccess = (event) => {
          resolve(event.target.result || null);
        };
      });
    } catch (error) {
      console.error(i18n.t('editor:utils.indexedDB.getSnapshotError'), error);
      throw error;
    }
  }

  /**
   * 删除截图数据
   * @param {string} id - 截图ID
   * @returns {Promise<boolean>} 返回是否删除成功
   */
  async deleteSnapshot(id) {
    try {
      const db = await this.initDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['snapshots'], 'readwrite');
        const store = transaction.objectStore('snapshots');
        const request = store.delete(id);
        
        request.onerror = (event) => {
          console.error(i18n.t('editor:utils.indexedDB.deleteSnapshotFailed'), event.target.error);
          reject(event.target.error);
        };
        
        request.onsuccess = () => {
          resolve(true);
        };
      });
    } catch (error) {
      console.error(i18n.t('editor:utils.indexedDB.deleteSnapshotError'), error);
      throw error;
    }
  }

  /**
   * 清空所有截图数据
   * @returns {Promise<boolean>} 返回是否清空成功
   */
  async clearAllSnapshots() {
    try {
      const db = await this.initDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['snapshots'], 'readwrite');
        const store = transaction.objectStore('snapshots');
        const request = store.clear();
        
        request.onerror = (event) => {
          console.error(i18n.t('editor:utils.indexedDB.clearSnapshotFailed'), event.target.error);
          reject(event.target.error);
        };
        
        request.onsuccess = () => {
          resolve(true);
        };
      });
    } catch (error) {
      console.error(i18n.t('editor:utils.indexedDB.clearSnapshotError'), error);
      throw error;
    }
  }
}

// 创建单例实例
const dbManager = new IndexedDBManager();
export default dbManager; 